import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  addDays,
  addMonths,
  addWeeks,
  format,
  parse,
} from 'date-fns'
import { api, type Appointment } from '@/lib/api'
import { ApptForm, ApptFormFields } from './ApptForm'
import { AppointmentDetailPanel } from './AppointmentDetailPanel'
import { BottomSheet } from './BottomSheet'
import { CalendarToolbar } from './CalendarToolbar'
import { DayView } from './DayView'
import { EditApptForm, EditApptFormFields } from './EditApptForm'
import { MonthView } from './MonthView'
import { RescheduleDialog } from './RescheduleDialog'
import { WeekView } from './WeekView'
import { formatTime, getMonthGridDays, parseTime } from './calendarHelpers'
import { useCalendar, type CalendarView } from './useCalendar'
import { useSwipe } from './useSwipe'

const MOBILE_BREAKPOINT = 768

interface NewApptDefaults {
  defaultDate?: string
  defaultStartTime?: string
  defaultEndTime?: string
}

/** 桌面右侧面板状态机：关闭 / 详情 / 编辑 / 新建 */
type PanelState =
  | { mode: 'closed' }
  | { mode: 'detail' | 'edit'; appt: Appointment }
  | { mode: 'create'; defaults: NewApptDefaults }

function getInitialView(searchParams: URLSearchParams, isMobile: boolean): CalendarView {
  if (isMobile) return 'day'
  const v = searchParams.get('view')
  if (v === 'month' || v === 'week' || v === 'day') return v
  return 'week'
}

function getInitialDate(searchParams: URLSearchParams): Date {
  const d = searchParams.get('date')
  if (d) {
    const parsed = parse(d, 'yyyy-MM-dd', new Date())
    if (!Number.isNaN(parsed.getTime())) return parsed
  }
  return new Date()
}

export function CalendarContainer() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()

  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches
  })

  const [view, setView] = useState<CalendarView>(() => getInitialView(searchParams, isMobile))
  const [currentDate, setCurrentDate] = useState<Date>(() => getInitialDate(searchParams))

  // 移动端沿用 Dialog/ BottomSheet 的 state
  const [showNewForm, setShowNewForm] = useState(false)
  const [newDefaults, setNewDefaults] = useState<NewApptDefaults>({})
  const [editingAppt, setEditingAppt] = useState<Appointment | null>(null)
  const [bottomSheetAppt, setBottomSheetAppt] = useState<Appointment | null>(null)

  // 桌面右侧面板
  const [panel, setPanel] = useState<PanelState>({ mode: 'closed' })
  const closePanel = () => setPanel({ mode: 'closed' })

  const [rescheduleTarget, setRescheduleTarget] = useState<{
    appt: Appointment
    date: Date
    startTime: string
    endTime: string
  } | null>(null)
  const [rescheduleWarning, setRescheduleWarning] = useState<string | null>(null)

  // 监听移动端变化
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`)
    const handler = (e: MediaQueryListEvent) => {
      setIsMobile(e.matches)
      if (e.matches) setView('day')
    }
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [])

  // URL 同步
  useEffect(() => {
    const params: Record<string, string> = {}
    if (view !== 'week') params.view = view
    const dateStr = format(currentDate, 'yyyy-MM-dd')
    const todayStr = format(new Date(), 'yyyy-MM-dd')
    if (dateStr !== todayStr) params.date = dateStr
    setSearchParams(params, { replace: true })
  }, [view, currentDate, setSearchParams])

  // ESC 关闭桌面面板（调课确认弹窗打开时不拦截，由弹窗自身处理）
  useEffect(() => {
    if (panel.mode === 'closed') return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !rescheduleTarget) closePanel()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [panel.mode, rescheduleTarget])

  const { items, byDay, isLoading } = useCalendar(view, currentDate)

  const { data: clientData } = useQuery({
    queryKey: ['clients'],
    queryFn: () => api.getClients(),
    staleTime: 5 * 60 * 1000,
  })
  const clients = clientData?.items || []
  const clientsMap = useMemo(() => new Map(clients.map((c) => [c.id, c])), [clients])

  const monthDays = useMemo(() => getMonthGridDays(currentDate), [currentDate])

  /** 更新所有 appointments 缓存（增删改后即时同步，避免 refetch） */
  function updateApptCache(fn: (items: any[]) => any[]) {
    qc.setQueriesData<any>({ queryKey: ['appointments'] }, (old: any) => {
      if (!old) return old
      return { ...old, items: fn(old.items || []) }
    })
  }

  const createAppt = useMutation({
    mutationFn: api.createAppointment,
    onSuccess: (data) => {
      // 直接加入缓存，与其他查询数据保持一致（保持 appointment 扁平的顶层字段 + 嵌套兼容）
      const created = (data as any)?.appointment || (data as any)
      if (created?.id) {
        updateApptCache((items) => [...items, created])
      }
      setShowNewForm(false)
      closePanel()
    },
    onError: (err) => {
      alert((err as Error).message)
    },
  })

  const updateAppt = useMutation({
    mutationFn: ({ id, body }: { id: number; body: { title?: string; location?: string; notes?: string } }) =>
      api.updateAppointment(id, body),
    onSuccess: (_data, vars) => {
      updateApptCache((items) => items.map((a) => a.id === vars.id ? { ...a, ...vars.body } : a))
      setEditingAppt(null)
      closePanel()
    },
  })

  const completeAppt = useMutation({
    mutationFn: api.completeAppointment,
    onSuccess: (data, vars) => {
      updateApptCache((items) => items.map((a) => a.id === vars ? { ...a, status: 'completed' } : a))
      qc.invalidateQueries({ queryKey: ['packages'] })
      qc.invalidateQueries({ queryKey: ['clients'] })
      qc.invalidateQueries({ queryKey: ['client-packages'] })
      setEditingAppt(null)
      closePanel()
      if (data?.warning === 'package_depleted') {
        alert(t('schedule.completePackageDepletedWarning'))
      }
    },
  })

  const noShowAppt = useMutation({
    mutationFn: api.noShowAppointment,
    onSuccess: (_data, vars) => {
      updateApptCache((items) => items.map((a) => a.id === vars ? { ...a, status: 'no_show' } : a))
      qc.invalidateQueries({ queryKey: ['client-packages'] })
      setEditingAppt(null)
      closePanel()
    },
  })

  const deleteAppt = useMutation({
    mutationFn: api.deleteAppointment,
    onSuccess: (_data, vars) => {
      updateApptCache((items) => items.filter((a) => a.id !== vars))
      qc.invalidateQueries({ queryKey: ['client-packages'] })
      setEditingAppt(null)
      closePanel()
    },
  })

  const moveAppt = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: number
      body: { scheduledDate: number; startTime: string; endTime: string }
    }) => api.moveAppointment(id, body),
    onSuccess: (_data, vars) => {
      updateApptCache((items) => items.map((a) => a.id === vars.id ? { ...a, ...vars.body } : a))
      setRescheduleTarget(null)
      closePanel()
    },
    onError: (err) => {
      alert((err as Error).message)
    },
  })

  const swipeHandlers = useSwipe({
    onLeft: () => handleNavigate(1),
    onRight: () => handleNavigate(-1),
  })

  function handleNavigate(amount: number) {
    if (view === 'day') setCurrentDate((d) => addDays(d, amount))
    else if (view === 'week') setCurrentDate((d) => addWeeks(d, amount))
    else setCurrentDate((d) => addMonths(d, amount))
  }

  function handleToday() {
    setCurrentDate(new Date())
  }

  function handleViewChange(v: CalendarView) {
    if (isMobile) return
    setView(v)
  }

  function openNewForm(date: Date, startTime?: string) {
    const dateStr = format(date, 'yyyy-MM-dd')
    const start = startTime || '10:00'
    const end = formatTime(parseTime(start) + 60)
    const defaults = { defaultDate: dateStr, defaultStartTime: start, defaultEndTime: end }
    if (isMobile) {
      setNewDefaults(defaults)
      setShowNewForm(true)
    } else {
      setPanel({ mode: 'create', defaults })
    }
  }

  function handleSlotClick(date: Date, time: string) {
    if (isMobile) return
    // 面板开启时点空白格 = 切换为新建（按新格子预填）
    openNewForm(date, time)
  }

  function handleEventClick(appt: Appointment) {
    if (isMobile) {
      setBottomSheetAppt(appt)
    } else {
      setPanel({ mode: 'detail', appt })
    }
  }

  function handleEventDrop(appt: Appointment, date: Date, time: string) {
    const duration = parseTime(appt.endTime) - parseTime(appt.startTime)
    const startMinutes = parseTime(time)
    const endMinutes = startMinutes + duration
    setRescheduleTarget({
      appt,
      date,
      startTime: time,
      endTime: formatTime(endMinutes),
    })
  }

  // ── OPT-028 ── 冲突检测（新建/改期各自独立，互不影响）──
  const clientById = new Map(clients.map((c: any) => [c.id, c.name]))

  /** 从缓存取冲突数据（两个函数共用纯数据查询，不含任何决策逻辑） */
  function getConflictsFromCache(scheduledDate: number, startTime: string, endTime: string, excludeId?: number) {
    const allData = qc.getQueriesData<any>({ queryKey: ['appointments'] })
    const allItems = allData.flatMap(([, d]) => d?.items || [])
    const targetDay = Math.floor(scheduledDate / 86400)
    return allItems.filter((a: any) => {
      if (a.status !== 'scheduled') return false
      if (excludeId && a.id === excludeId) return false
      if (Math.floor(a.scheduledDate / 86400) !== targetDay) return false
      return (a.startTime || '') < endTime && (a.endTime || '') > startTime
    })
  }

  /** 新建预约：window.confirm 弹窗 */
  function checkNewConflicts(scheduledDate: number, startTime: string, endTime: string, location?: string): boolean {
    const conflicts = getConflictsFromCache(scheduledDate, startTime, endTime, undefined)
    if (conflicts.length === 0) return true
    const c = conflicts[0]!
    const name = clientById.get(c.clientId!) || `#${c.clientId}`
    let msg = t('schedule.conflictWarning', { name, time: `${c.startTime}-${c.endTime}` })
    const locA = (location || '').trim()
    const mismatch = locA ? conflicts.find((x: any) => { const l = (x.location || '').trim(); return l && l !== locA }) : undefined
    if (mismatch) msg += '\n' + t('schedule.conflictLocationMismatch', { locA, locB: (mismatch.location || '').trim() })
    return window.confirm(msg)
  }

  /** 改期：弹窗内红色文字提醒，首次不提交，再次提交 */
  function checkRescheduleConflicts(
    appt: Appointment, date: Date, startTime: string, endTime: string
  ): boolean {
    const sd = Math.floor(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 1000)
    const conflicts = getConflictsFromCache(sd, startTime, endTime, appt.id)
    if (conflicts.length === 0) return true
    const c = conflicts[0]!
    const name = clientById.get(c.clientId!) || `#${c.clientId}`
    let msg = t('schedule.conflictWarning', { name, time: `${c.startTime}-${c.endTime}` })
    const locA = (appt.location || '').trim()
    const mismatch = locA ? conflicts.find((x: any) => { const l = (x.location || '').trim(); return l && l !== locA }) : undefined
    if (mismatch) msg += '\n' + t('schedule.conflictLocationMismatch', { locA, locB: (mismatch.location || '').trim() })
    if (!rescheduleWarning) { setRescheduleWarning(msg); return false }
    return true
  }

  function handleRescheduleConfirm(
    appt: Appointment,
    date: Date,
    startTime: string,
    endTime: string
  ) {
    if (!checkRescheduleConflicts(appt, date, startTime, endTime)) return
    setRescheduleWarning(null)
    setRescheduleTarget(null)
    moveAppt.mutate({
      id: appt.id,
      body: {
        scheduledDate: Math.floor(date.getTime() / 1000),
        startTime,
        endTime,
      },
    })
  }

  /** 面板/移动端"更改时间"入口：预填当前时间，打开可编辑的确认弹窗 */
  function openMoveDialog(appt: Appointment) {
    setRescheduleTarget({
      appt,
      date: new Date(appt.scheduledDate * 1000),
      startTime: appt.startTime,
      endTime: appt.endTime,
    })
  }

  function handleDayClick(date: Date) {
    setCurrentDate(date)
    setView(isMobile ? 'day' : 'week')
  }

  function handleMoreClick(date: Date) {
    setCurrentDate(date)
    setView(isMobile ? 'day' : 'week')
  }

  const panelAppt = panel.mode === 'detail' || panel.mode === 'edit' ? panel.appt : null

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] md:h-[calc(100vh-3rem)] min-h-[500px]" data-testid="calendar-container">
      <CalendarToolbar
        view={view}
        currentDate={currentDate}
        isMobile={isMobile}
        onViewChange={handleViewChange}
        onNavigate={handleNavigate}
        onToday={handleToday}
        onNew={() => openNewForm(currentDate)}
      />

      {/* 左右并排：日历 flex-1 + 右侧常驻面板列（长期占位，日历宽度固定不挪动） */}
      <div className="flex-1 flex min-h-0 gap-3">
        <div
          data-testid="calendar-body"
          className="flex-1 min-w-0 min-h-0 overflow-hidden border rounded-lg bg-card"
        >
          {isLoading ? (
            <div className="h-full flex items-center justify-center text-slate-500">{t('common.loading')}</div>
          ) : view === 'day' ? (
            <DayView
              date={currentDate}
              appointments={items}
              clientsMap={clientsMap}
              isMobile={isMobile}
              swipeHandlers={isMobile ? swipeHandlers : undefined}
              onEventClick={handleEventClick}
              onSlotClick={handleSlotClick}
              onEventDrop={isMobile ? undefined : handleEventDrop}
            />
          ) : view === 'week' ? (
            <WeekView
              date={currentDate}
              byDay={byDay}
              clientsMap={clientsMap}
              onEventClick={handleEventClick}
              onSlotClick={handleSlotClick}
              onEventDrop={handleEventDrop}
            />
          ) : (
            <MonthView
              date={currentDate}
              days={monthDays}
              byDay={byDay}
              clientsMap={clientsMap}
              onEventClick={handleEventClick}
              onDayClick={handleDayClick}
              onMoreClick={handleMoreClick}
            />
          )}
        </div>

        {/* 桌面右侧面板列：常驻占位，详情 / 编辑 / 新建 / 空态（移动端走 Dialog + BottomSheet） */}
        {!isMobile && (
          <aside
            data-testid="appt-panel"
            className="w-[320px] shrink-0 border rounded-lg bg-card overflow-hidden flex flex-col"
          >
            {panel.mode === 'closed' && (
              <div
                data-testid="appt-panel-empty"
                className="h-full flex items-center justify-center px-6 text-center text-sm text-slate-400 whitespace-pre-line"
              >
                {t('schedule.panel.empty')}
              </div>
            )}
            {panel.mode === 'detail' && panelAppt && (
              <AppointmentDetailPanel
                appt={panelAppt}
                client={panelAppt.clientId ? clientsMap.get(panelAppt.clientId) : undefined}
                onClose={closePanel}
                onEdit={() => setPanel({ mode: 'edit', appt: panelAppt })}
                onComplete={() => completeAppt.mutate(panelAppt.id)}
                onNoShow={() => noShowAppt.mutate(panelAppt.id)}
                onMove={() => openMoveDialog(panelAppt)}
                onDelete={() => deleteAppt.mutate(panelAppt.id)}
              />
            )}
            {panel.mode === 'edit' && panelAppt && (
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between px-4 py-3 border-b">
                  <h2 className="font-medium">{t('schedule.editTitle')}</h2>
                  <button
                    type="button"
                    data-testid="panel-close"
                    aria-label={t('common.close')}
                    className="text-slate-400 hover:text-slate-600 text-lg leading-none px-1"
                    onClick={closePanel}
                  >
                    ×
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto px-4 py-3">
                  <EditApptFormFields
                    key={panelAppt.id}
                    appt={panelAppt}
                    onClose={closePanel}
                    onSubmit={(body) => updateAppt.mutate({ id: panelAppt.id, body })}
                    onDelete={() => deleteAppt.mutate(panelAppt.id)}
                    onComplete={() => completeAppt.mutate(panelAppt.id)}
                    onNoShow={() => noShowAppt.mutate(panelAppt.id)}
                    onMove={() => openMoveDialog(panelAppt)}
                  />
                </div>
              </div>
            )}
            {panel.mode === 'create' && (
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between px-4 py-3 border-b">
                  <h2 className="font-medium">{t('schedule.new')}</h2>
                  <button
                    type="button"
                    data-testid="panel-close"
                    aria-label={t('common.close')}
                    className="text-slate-400 hover:text-slate-600 text-lg leading-none px-1"
                    onClick={closePanel}
                  >
                    ×
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto px-4 py-3">
                  <ApptFormFields
                    // key 随点中格子变化：强制重挂载，表单日期/时间初始化为新格子的值
                    key={`${panel.defaults.defaultDate ?? 'none'}-${panel.defaults.defaultStartTime ?? ''}`}
                    clients={clients}
                    {...panel.defaults}
                    onClose={closePanel}
                    onSubmit={(body) => {
                      if (!checkNewConflicts(body.scheduledDate, body.startTime, body.endTime, body.location)) return
                      createAppt.mutate(body)
                    }}
                  />
                </div>
              </div>
            )}
          </aside>
        )}
      </div>

      {/* 移动端：新建/编辑仍为 Dialog */}
      {isMobile && (
        <>
          <ApptForm
            key={`${newDefaults.defaultDate ?? 'none'}-${newDefaults.defaultStartTime ?? ''}`}
            open={showNewForm}
            clients={clients}
            {...newDefaults}
            onClose={() => setShowNewForm(false)}
            onSubmit={(body) => {
              const d = new Date(body.scheduledDate * 1000).toISOString().split('T')[0]!
              if (!checkNewConflicts(body.scheduledDate, body.startTime, body.endTime, body.location)) return
              createAppt.mutate(body)
            }}
          />
          <EditApptForm
            key={editingAppt?.id ?? 'none'}
            open={!!editingAppt}
            appt={editingAppt}
            onClose={() => setEditingAppt(null)}
            onSubmit={(body) => editingAppt && updateAppt.mutate({ id: editingAppt.id, body })}
            onDelete={() => editingAppt && deleteAppt.mutate(editingAppt.id)}
            onComplete={() => editingAppt && completeAppt.mutate(editingAppt.id)}
            onNoShow={() => editingAppt && noShowAppt.mutate(editingAppt.id)}
            onMove={() => editingAppt && openMoveDialog(editingAppt)}
          />
        </>
      )}

      <RescheduleDialog
        open={!!rescheduleTarget}
        appointment={rescheduleTarget?.appt || null}
        targetDate={rescheduleTarget?.date || null}
        targetStartTime={rescheduleTarget?.startTime || ''}
        targetEndTime={rescheduleTarget?.endTime || ''}
        onClose={() => { setRescheduleTarget(null); setRescheduleWarning(null) }}
        onConfirm={handleRescheduleConfirm}
        warning={rescheduleWarning}
        isLoading={moveAppt.isPending}
      />

      <BottomSheet
        open={!!bottomSheetAppt}
        appointment={bottomSheetAppt}
        onClose={() => setBottomSheetAppt(null)}
        onView={() => {
          if (bottomSheetAppt) setEditingAppt(bottomSheetAppt)
        }}
        onReschedule={
          bottomSheetAppt?.status === 'scheduled'
            ? () => openMoveDialog(bottomSheetAppt)
            : undefined
        }
        onComplete={
          bottomSheetAppt?.status === 'scheduled'
            ? () => {
                if (bottomSheetAppt) completeAppt.mutate(bottomSheetAppt.id)
              }
            : undefined
        }
      />
    </div>
  )
}
